"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { regionFromPathname } from "@/lib/region";
import { richParagraphs } from "@/lib/richtext";
import { cn } from "@/lib/utils";
import { usePublicPathname } from "@/lib/public-pathname";

type TimelineStepBlok = SbBlokData & {
  marker?: string;
  title?: string;
  description?: unknown;
};

type TreatmentTimelineBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  footnote?: string;
  steps?: TimelineStepBlok[];
  bg_color?: unknown;
  card_color?: unknown;
};

export default function TreatmentTimeline({ blok }: { blok: TreatmentTimelineBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const surface = surfaceFrom(blok.bg_color);
  const card = surfaceFrom(blok.card_color);
  const steps = (blok.steps ?? []).filter((s) => s.marker || s.title || s.description);
  if (steps.length === 0) return null;

  const lgCols =
    steps.length >= 5
      ? "lg:grid-cols-5"
      : steps.length === 4
        ? "lg:grid-cols-4"
        : steps.length === 3
          ? "lg:grid-cols-3"
          : "lg:grid-cols-2";

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-12">
        {(blok.heading || blok.intro) && (
          <div className="flex flex-col gap-5">
            {blok.heading && (
              <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
                {blok.heading}
              </h2>
            )}
            {blok.intro && (
              <p
                className={cn(
                  "max-w-[943px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl",
                  onSurface(surface, "text-white/85", "text-grey-08", "text-grey-08"),
                )}
              >
                {blok.intro}
              </p>
            )}
          </div>
        )}

        <ol className={cn("relative grid gap-6 lg:gap-5", lgCols)}>
          <span
            aria-hidden
            className="bg-teal-03 absolute top-4 right-0 left-0 hidden h-px lg:block"
          />
          {steps.map((step) => (
            <li
              key={step._uid as string}
              {...storyblokEditable(step)}
              className="relative flex flex-col gap-4"
            >
              {step.marker && (
                <span className="bg-teal-07 z-10 w-fit rounded-full px-4 py-1 text-sm font-bold text-white">
                  {step.marker}
                </span>
              )}
              <div
                style={card ? { backgroundColor: card.backgroundColor } : undefined}
                className={cn(
                  "flex flex-1 flex-col gap-2 rounded-2xl p-5 lg:p-6",
                  card ? "" : "border-grey-01 border",
                )}
              >
                {step.title && (
                  <h3
                    className={cn(
                      "text-h6 font-semibold tracking-[-0.01em]",
                      onSurface(card ?? surface, "text-white", "text-grey-09", "text-grey-09"),
                    )}
                  >
                    {step.title}
                  </h3>
                )}
                {richParagraphs(step.description, region, (card ?? surface)?.onDark).map(
                  (para, i) => (
                    <p
                      key={i}
                      className={cn(
                        "text-base leading-relaxed",
                        onSurface(card ?? surface, "text-white/85", "text-grey-08", "text-grey-08"),
                      )}
                    >
                      {para}
                    </p>
                  ),
                )}
              </div>
            </li>
          ))}
        </ol>

        {blok.footnote && (
          <p
            className={cn(
              "text-base leading-relaxed",
              onSurface(surface, "text-white/70", "text-grey-06", "text-grey-06"),
            )}
          >
            {blok.footnote}
          </p>
        )}
      </div>
    </section>
  );
}
